import React, { useState, useEffect, useCallback } from 'react'

import {
  createFirstTripEvent,
  dismissCreateFirstTripEvent,
  saveToTripEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Modal from 'components/Luxkit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { CreateTripError } from 'tripPlanner/api/types'
import NameTrip from 'tripPlanner/components/EntryPoints/Common/Fields/NameTrip'
import TripSelect from 'tripPlanner/components/EntryPoints/ModalChildren/TripSelect'
import { useCreateTrip, useEditableTrips } from 'tripPlanner/hooks/api'
import { FullTrip, BasicTrip } from 'tripPlanner/types/common'
import { ItemLabel } from 'tripPlanner/types/tripItem'
import { getDefaultTripImageId } from 'tripPlanner/utils'

const titleOptions = {
  hasTrips: 'Save to trip plan',
  createTrip: 'Name your new trip',
  flight: 'Save this flight to your first trip plan',
  offer: 'Save this offer to your first trip plan',
} as const

export type TripPlannerSaveModalResult =
  | {
      status: 'dismissed'
    }
  | {
      status: 'created'
      trip: BasicTrip | FullTrip
    }

interface Props {
  createTripItems: (trip: BasicTrip | FullTrip) => any
  offerType: ItemLabel
  offerImageId?: string
  defaultTripName?: string
  currentTripId?: string
  isCreatingItem: boolean
}

type ModalState = 'create' | 'select'

function SaveModal({
  defaultTripName = '',
  createTripItems,
  offerType,
  offerImageId,
  isCreatingItem,
}: Props) {
  const { data, isFetching } = useEditableTrips()
  const trips = data as BasicTrip[]
  const hasTrips = trips.length > 0

  const [tripName, setTripName] = useState(defaultTripName)
  const [errorMsg, setErrorMsg] = useState('')
  const [modalView, setModalView] = useState<ModalState>(
    trips.length > 0 ? 'select' : 'create',
  )

  const isCreateModal = modalView === 'create' && !isFetching

  const newTripOfferTitle = hasTrips
    ? modalView == 'create'
      ? titleOptions.createTrip
      : titleOptions.hasTrips
    : offerType === 'Flight'
    ? titleOptions.flight
    : titleOptions.offer

  const modalElement = useModalElementContext<TripPlannerSaveModalResult>()

  const saveTripItems = useCallback(
    async (trip: FullTrip | BasicTrip) => {
      await createTripItems(trip)
      modalElement.resolve({ status: 'created', trip })
    },
    [createTripItems, modalElement],
  )

  useEffect(() => {
    if (modalElement.open) {
      setModalView(hasTrips ? 'select' : 'create')
    }
  }, [hasTrips, modalElement.open])

  useEffect(() => {
    if (isCreateModal) {
      setErrorMsg('')
    }
  }, [isCreateModal])

  const { mutate: createTrip, isLoading: isCreatingTrip } = useCreateTrip({
    onSuccess: (trip) => {
      saveTripItems(trip)
    },
    onError: (e) => {
      const error = e as CreateTripError

      if (error.code === 'TripNameConflict') {
        setErrorMsg('Trip name already exists')
      } else {
        setErrorMsg('Unknown server error')
      }
    },
  })

  const isTripNameEmpty = tripName.length === 0
  const createNewTrip = useCallback(async () => {
    if (isTripNameEmpty) {
      setErrorMsg('The trip name field cannot be empty')
    } else {
      fireInteractionEvent(
        saveToTripEvent('modal_new_trip_name', 'global', 'save'),
      )
      if (!hasTrips) {
        fireInteractionEvent(createFirstTripEvent(tripName))
      }
      createTrip({
        name: tripName,
        imageId: offerImageId || getDefaultTripImageId(trips),
      })
    }
  }, [createTrip, offerImageId, tripName, trips, isTripNameEmpty, hasTrips])

  const isLoading = isCreatingTrip || isCreatingItem || isFetching
  const onModalClose = useCallback(() => {
    fireInteractionEvent(
      saveToTripEvent(
        isCreateModal ? 'modal_new_trip_name' : 'modal',
        'global',
        'dismiss',
      ),
    )
    if (!hasTrips) {
      fireInteractionEvent(dismissCreateFirstTripEvent())
    }
    modalElement.resolve({ status: 'dismissed' })
  }, [modalElement, isCreateModal, hasTrips])

  const onCreateSelect = useCallback(() => {
    fireInteractionEvent(saveToTripEvent('modal_new_trip', 'global', 'click'))
    setModalView('create')
  }, [])

  const onSelectTrip = useCallback(
    (trip: FullTrip | BasicTrip) => {
      fireInteractionEvent(
        saveToTripEvent('modal_existing_trip', 'global', 'click'),
      )
      saveTripItems(trip)
    },
    [saveTripItems],
  )

  const saveButtonTitle =
    modalView == 'create' ? 'Create trip plan' : 'Save to trip plan'

  return (
    <Modal
      title={isLoading ? '' : newTripOfferTitle}
      onClose={onModalClose}
      primaryActionText={isCreateModal ? saveButtonTitle : undefined}
      secondaryActionText={isCreateModal ? 'Cancel' : undefined}
      onPrimaryActionClick={isCreateModal ? createNewTrip : undefined}
      onSecondaryActionClick={isCreateModal ? onModalClose : undefined}
      isPrimaryActionDisabled={isLoading}
      isSecondaryActionDisabled={isLoading}
      size={isCreateModal && !hasTrips ? 'large' : 'small'}
    >
      {modalView === 'select' && !isLoading && (
        <TripSelect
          onCreateSelect={onCreateSelect}
          onSelectTrip={onSelectTrip}
          trips={trips}
        />
      )}
      {isCreateModal && !isLoading && (
        <NameTrip
          setErrorMsg={setErrorMsg}
          setTripName={setTripName}
          firstTime={!hasTrips}
          tripName={tripName}
          errorMsg={errorMsg}
          onSubmit={createNewTrip}
        />
      )}
      {isLoading && <LoadingIndicator floating />}
    </Modal>
  )
}

export default SaveModal
